import React, { useEffect } from "react";
import { Suspense } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import SubstepNode from "./SubstepNode";
import ChildrenRenderer from "./ChildrenRenderer";
import RenderHtml from "../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import PartsTable from "../Tables/PartsTable";
import { pingTask } from "../../../../util/apiService";
import { bomRowsMapper, StepDetailsMapper } from "../../Utils/utils";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { SharedHistoryContext } from "../../../../common/RichTextEditor/LexicalEditor/context/SharedHistoryContext";
import { TableContext } from "../../../../common/RichTextEditor/LexicalEditor/plugins/TablePlugin";
import { ToolbarContext } from "../../../../common/RichTextEditor/LexicalEditor/context/ToolbarContext";
import Editor from "../../../../common/RichTextEditor/Editor/Editor";
import { buildImportMap } from "../../../../util/lexical";
import PlaygroundNodes from "../../../../common/RichTextEditor/LexicalEditor/nodes/PlaygroundNodes";
import PlaygroundEditorTheme from "../../../../common/RichTextEditor/LexicalEditor/themes/PlaygroundEditorTheme";
import {
  getHaloWrapperStyle,
  getHaloCheckboxStyle,
  isDescendantLockedPreview,
  getDescendantLockedCheckboxStyle,
  auditPreviewSectionDescendantWrapStyle,
} from "./nodeSelectionCheckboxStyles";

const StepNode = ({
  step,
  stepIndex,
  globalStepNumber,
  sectionIndex,
  sectionHeading,
  sectionId,
  documentType = "",
  documentPartNumber = "",
  documentRevision = "",
  onNavigate,
  checkboxState,
  disabled = false,
  showCheckbox = true,
  showOnlySectionCheckboxes = false,
  checkboxLabelColor = null,
  allNodesData = [],
  hideChildCheckboxes = false,
  forceShowChildHeadings = false,
  hideSubstepCheckboxes = false,
  isLock = false,
  isRevisionLocked = false,
  hideChildHeadingsForTypes = null,
  trainedStepNumbers = null,
  partiallyTrainedStepNumbers = null,
  allStepsTrained = false,
  totalQtyToBuild = null,
  enableCanvasStagePopup = false,
}) => {
  const authUser = useSelector((state) => state.auth.user);
  const stepHeading = `Step ${step.is_deleted ? "(Deleted)" : globalStepNumber}`;

  const isStepFullyTrained =
    !step.is_deleted &&
    (allStepsTrained ||
      (trainedStepNumbers?.has ? trainedStepNumbers.has(globalStepNumber) : false));

  const isStepPartiallyTrained =
    !step.is_deleted &&
    !isStepFullyTrained &&
    (partiallyTrainedStepNumbers?.has
      ? partiallyTrainedStepNumbers.has(globalStepNumber)
      : false);

  /** Read-only checkbox only when fully trained for this revision. */
  const isStepTrained = isStepFullyTrained;

  /** Any training recorded in latest log (full or partial) — same green "Trained" UI as reference. */
  const hasTrainingLoggedForStep = isStepFullyTrained || isStepPartiallyTrained;

  /** Give Training only — mint checkmark flow from Document (`checkboxState.giveTrainingCheckmark`). */
  const isGiveTrainingCheckmarkFlow =
    checkboxState?.giveTrainingCheckmark === true;

  // Training tags: Get Training when log sets are loaded; Give Training when in that flow (uses checkmarks below).
  const shouldShowTrainingTags =
    !step.is_deleted &&
    (allStepsTrained ||
      (trainedStepNumbers !== null && partiallyTrainedStepNumbers !== null) ||
      isGiveTrainingCheckmarkFlow);
  
  // Get revisionId and authUser for ping API
  const location = useLocation();
  const params = useParams();
  
  // Extract revisionId from params (could be revisionId or revision_id depending on route)
  const revisionId = params.revisionId || params.revision_id;
  
  // Check if we're on a view-revision route
  const isViewRevisionRoute = location.pathname.includes('/view-revision') || 
                               location.pathname.includes('/view-section') ||
                               location.pathname.includes('/view-step') ||
                               location.pathname.includes('/view-substep') ||
                               location.pathname.includes('/view-childnode');
  
  // Start ping interval if on view-revision route
  useEffect(() => {
    if (!isViewRevisionRoute || !revisionId || !authUser) return;
    
    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: revisionId,
          user_id: authUser.id
        };
        await pingTask(pingData);
      } catch (error) {
        console.error('Ping failed in StepNode:', error);
      }
    }, 60000); // 60 seconds = 1 minute
    
    return () => {
      clearInterval(interval);
    };
  }, [isViewRevisionRoute, revisionId, authUser]);

  // Determine checkbox state for this step (single current, copy/source, or training multi-select)
  const selectedStepIds = checkboxState?.selectedStepIds;
  const isInTrainingMultiSelection =
    Array.isArray(selectedStepIds) &&
    selectedStepIds.includes(step.tree_node_id);
  const isCurrent =
    isInTrainingMultiSelection ||
    (checkboxState?.currentNodeId === step.tree_node_id &&
      checkboxState?.currentNodeType === "step");
  const isSourceStep = checkboxState?.sourceNodeId && (
    checkboxState?.sourceNodeId === step.tree_node_id ||
    checkboxState?.sourceNodeId === step.step_id_ref
  );
  const isChecked =
    !!(isCurrent || checkboxState?.allChildrenChecked || isSourceStep);
  const isDescendantLocked = isDescendantLockedPreview(
    checkboxState,
    isCurrent,
    isChecked,
    !!isSourceStep,
  );

  /** Below-the-line "Trained" / "Not Trained" — always from latest log sets (Give + Get). Checkbox state is selection, not training status. */
  const displayTrainingTagAsTrained = hasTrainingLoggedForStep;

  /** Give Training: any step in the log (full or partial) is locked green; Get Training: only fully trained is read-only. */
  const giveTrainingReadonlyFromLog =
    isGiveTrainingCheckmarkFlow && hasTrainingLoggedForStep;
  const isTrainingCheckboxReadonly =
    giveTrainingReadonlyFromLog ||
    (!isGiveTrainingCheckmarkFlow && isStepTrained);

  // Keep native checkbox enabled so browser doesn't force it grey,
  // but make it non-interactive + low-opacity when trained.
  const isDisabled =
    disabled ||
    (checkboxState?.allChildrenDisabled && !isCurrent) ||
    (isRevisionLocked &&
      isRevisionLocked.lock &&
      isRevisionLocked.lock.user_id !== authUser?.id);
  const isStepInteractionBlocked =
    isDisabled || isSourceStep || isTrainingCheckboxReadonly;

  const focusIdStep = checkboxState?.currentNodeId;
  const stepDetail = step.detail || {};
  const stepIdsMatch =
    !!focusIdStep &&
    (focusIdStep === step.tree_node_id ||
      focusIdStep === step.step_id_ref ||
      focusIdStep === stepDetail.step_tree_node_id ||
      focusIdStep === stepDetail.procedure_tree_ref);
  const isAuditStepFocus =
    checkboxState?.currentNodeType === "step" &&
    stepIdsMatch &&
    !isInTrainingMultiSelection;
  const useSelectionHalo =
    isChecked &&
    !isSourceStep &&
    !isGiveTrainingCheckmarkFlow &&
    !isStepTrained &&
    isAuditStepFocus &&
    checkboxState?.auditPreviewCurrentNode === true;

  const stepCheckboxDisabled = isDisabled || isTrainingCheckboxReadonly;
  const stepCheckboxOnChange = () => {
    if (isSourceStep) return;
    if (isTrainingCheckboxReadonly) return;
    if (!isDisabled && onNavigate) {
      onNavigate("step", step, {
        stepIndex,
        sectionIndex,
        sectionHeading,
        stepHeading,
        globalStepNumber,
        sectionId,
        stepId: step.tree_node_id,
      });
    }
  };

  // Data source based on locked state
  // const pastData = step.detail?.past_step_data_json;

  const stepDetailsData = step.detail?.step_data?.step_details;

  const bomTableTitle = step.detail?.step_data?.bom_table_title_info?.category_name;

  const partsRequiredData = step.detail?.step_data?.parts_required;

  const itemCodeColumnTitle = step.detail?.step_data?.part_number_column_title_ref_info?.category_name || "Item Code";

  /** Give Training "Checkmark Where Trained" — green = already in log; blue = newly selected (matches Get Training accent). */
  const GIVE_TRAINING_CHECKBOX_FILL = "#96C8A2";
  const GIVE_TRAINING_SELECTION_BLUE = "#0D6EFD";
  /** Empty / Not Trained: medium gray outline, white fill, softly rounded square (design ref). */
  const GIVE_TRAINING_UNCHECKED_BORDER = "#9ca3af";
  const GIVE_TRAINING_UNCHECKED_BG = "#ffffff";
  const GIVE_TRAINING_CHECKBOX_RADIUS = 3;
  const giveTrainingCheckmarkSvg = `url("data:image/svg+xml,${encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'><path d='M3.5 7.5l2.5 2.5 4.5-5.5' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>",
  )}")`;
  return (
    <div
      key={step.tree_node_id}
      data-export-block="step"
      style={{ marginTop: "20px", clear: "both" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          {showCheckbox && (
            <div
              style={{
                position: "relative",
                display: "inline-block",
                ...auditPreviewSectionDescendantWrapStyle(checkboxState),
              }}
            >
              {isDescendantLocked ? (
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled
                  readOnly
                  tabIndex={-1}
                  aria-readonly="true"
                  onChange={() => {}}
                  style={getDescendantLockedCheckboxStyle()}
                />
              ) : useSelectionHalo ? (
                <div style={getHaloWrapperStyle()}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={stepCheckboxDisabled}
                    onChange={stepCheckboxOnChange}
                    style={getHaloCheckboxStyle({
                      disabled: stepCheckboxDisabled,
                      isSource: !!isSourceStep,
                    })}
                  />
                </div>
              ) : (
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={stepCheckboxDisabled}
                  onChange={stepCheckboxOnChange}
                  style={
                    isGiveTrainingCheckmarkFlow && !isSourceStep
                      ? {
                          cursor: giveTrainingReadonlyFromLog
                            ? "not-allowed"
                            : isDisabled
                              ? "not-allowed"
                              : "pointer",
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                          width: 18,
                          height: 18,
                          minWidth: 18,
                          minHeight: 18,
                          flexShrink: 0,
                          boxSizing: "border-box",
                          borderRadius: GIVE_TRAINING_CHECKBOX_RADIUS,
                          border: isChecked
                            ? `1px solid ${
                                hasTrainingLoggedForStep
                                  ? GIVE_TRAINING_CHECKBOX_FILL
                                  : GIVE_TRAINING_SELECTION_BLUE
                              }`
                            : `1px solid ${GIVE_TRAINING_UNCHECKED_BORDER}`,
                          backgroundColor: isChecked
                            ? hasTrainingLoggedForStep
                              ? GIVE_TRAINING_CHECKBOX_FILL
                              : GIVE_TRAINING_SELECTION_BLUE
                            : GIVE_TRAINING_UNCHECKED_BG,
                          backgroundImage: isChecked ? giveTrainingCheckmarkSvg : "none",
                          backgroundSize: "12px 12px",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          opacity:
                            isChecked && hasTrainingLoggedForStep
                              ? 0.65
                              : isDisabled && !isChecked
                                ? 0.6
                                : 1,
                        }
                      : {
                          cursor: isStepTrained
                            ? "not-allowed"
                            : isDisabled
                              ? "not-allowed"
                              : isSourceStep
                                ? "not-allowed"
                                : "pointer",
                          accentColor: isSourceStep
                            ? "rgb(68, 68, 68)"
                            : isChecked
                              ? isStepFullyTrained || isStepPartiallyTrained
                                ? "#198754"
                                : "#0D6EFD"
                              : undefined,
                          backgroundColor: isSourceStep ? "rgb(68, 68, 68)" : undefined,
                          // Green (trained) checkboxes are read-only — muted with 0.6 like other disabled inputs
                          opacity:
                            isChecked && hasTrainingLoggedForStep
                              ? 0.6
                              : isDisabled && !isChecked
                                ? 0.6
                                : 1,
                        }
                  }
                />
              )}
              {/* Red cross icon overlay - shown when disabled and hovering */}
              {isDisabled && !isDescendantLocked && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    opacity: 0,
                    transition: "opacity 0.2s ease-in-out",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.opacity = "0";
                  }}
                  className="checkbox-hover-overlay"
                >
                  <FontAwesomeIcon
                    icon={faTimes}
                    size="sm"
                    color="red"
                    style={{
                      filter: "drop-shadow(0 0 1px rgba(255,255,255,0.8))",
                    }}
                  />
                </div>
              )}
            </div>
          )}
          <h2
            onClick={() => {
              // Prevent action for source step being moved
              if (isSourceStep) {
                return;
              }
              if (isTrainingCheckboxReadonly) return;
              if (!isDisabled && showCheckbox && onNavigate) {
                onNavigate("step", step, {
                  stepIndex,
                  sectionIndex,
                  sectionHeading,
                  stepHeading,
                  globalStepNumber,
                  sectionId,
                  stepId: step.tree_node_id,
                });
              }
            }}
            style={{
              fontSize: "20px",
              margin: 0,
              fontWeight: useSelectionHalo || isDescendantLocked ? 700 : undefined,
              color: useSelectionHalo || isDescendantLocked
                ? "#111"
                : showCheckbox
                  ? checkboxLabelColor || undefined
                  : undefined,
              cursor: isStepInteractionBlocked
                ? "not-allowed"
                : !isDisabled && showCheckbox
                  ? "pointer"
                  : "default",
              userSelect: "none",
            }}
          >
            {stepHeading}
          </h2>
        </div>

        <div
          style={{
            color: "#888",
            fontSize: "14px",
            fontWeight: "normal",
          }}
        >
          {!step.is_deleted && <>Section {sectionIndex + 1} &gt; Step {globalStepNumber}</>}
        </div>
      </div>
      <hr />
      {(() => {
        const rawShort = step.detail?.short_description || "";
        const contentHtml =
          typeof rawShort === "string" && rawShort.includes('class="editor-shell"')
            ? rawShort.replace(/^<div class="editor-shell">(.*)<\/div>\s*$/s, "$1")
            : rawShort && rawShort.trim() !== ""
              ? rawShort.includes('<') && rawShort.includes('>')
                ? rawShort
                : `<p>${rawShort.replace(/"/g, '&quot;')}</p>`
              : rawShort;

        const shortDescriptionBlock = contentHtml ? (
          <div style={{ marginTop: 0 }}>
            <div className="rich-text-content-display">
              <Suspense fallback={<div>Loading...</div>}>
                <LexicalComposer
                  initialConfig={{
                    editorState: null,
                    html: { import: buildImportMap() },
                    namespace: "ShortDescriptionViewer",
                    nodes: [...PlaygroundNodes],
                    onError: (error) => {
                      console.error("Error in Short Description viewer:", error);
                    },
                    theme: PlaygroundEditorTheme,
                    readOnly: true,
                    editable: false,
                  }}
                >
                  <SharedHistoryContext>
                    <TableContext>
                      <ToolbarContext>
                        <div className="editor-shell">
                          <Editor
                            handleEditorChange={() => {}}
                            initialContent={contentHtml}
                          />
                        </div>
                      </ToolbarContext>
                    </TableContext>
                  </SharedHistoryContext>
                </LexicalComposer>
              </Suspense>
            </div>
          </div>
        ) : null;

        const trainingStatusBlock =
          shouldShowTrainingTags &&
          (displayTrainingTagAsTrained ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                color: "#198754",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M16 8C16 10.1217 15.1571 12.1566 13.6569 13.6569C12.1566 15.1571 10.1217 16 8 16C5.87827 16 3.84344 15.1571 2.34315 13.6569C0.842855 12.1566 0 10.1217 0 8C0 5.87827 0.842855 3.84344 2.34315 2.34315C3.84344 0.842855 5.87827 0 8 0C10.1217 0 12.1566 0.842855 13.6569 2.34315C15.1571 3.84344 16 5.87827 16 8ZM12.03 4.97C11.9586 4.89882 11.8735 4.84277 11.7799 4.80522C11.6863 4.76766 11.5861 4.74936 11.4853 4.75141C11.3845 4.75347 11.2851 4.77583 11.1932 4.81717C11.1012 4.85851 11.0185 4.91797 10.95 4.992L7.477 9.417L5.384 7.323C5.24183 7.19052 5.05378 7.1184 4.85948 7.12183C4.66518 7.12525 4.47979 7.20397 4.34238 7.34138C4.20497 7.47879 4.12625 7.66418 4.12283 7.85848C4.1194 8.05278 4.19152 8.24083 4.324 8.383L6.97 11.03C7.04128 11.1012 7.12616 11.1572 7.21958 11.1949C7.313 11.2325 7.41305 11.2509 7.51375 11.2491C7.61444 11.2472 7.71374 11.2251 7.8057 11.184C7.89766 11.1429 7.9804 11.0837 8.049 11.01L12.041 6.02C12.1771 5.8785 12.2523 5.68928 12.2504 5.49296C12.2485 5.29664 12.1698 5.10888 12.031 4.97H12.03Z"
                  fill="#198754"
                />
              </svg>
              <span>Trained</span>
            </span>
          ) : (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                color: "#dc3545",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M16 8C16 10.1217 15.1571 12.1566 13.6569 13.6569C12.1566 15.1571 10.1217 16 8 16C5.87827 16 3.84344 15.1571 2.34315 13.6569C0.842855 12.1566 0 10.1217 0 8C0 5.87827 0.842855 3.84344 2.34315 2.34315C3.84344 0.842855 5.87827 0 8 0C10.1217 0 12.1566 0.842855 13.6569 2.34315C15.1571 3.84344 16 5.87827 16 8ZM5.354 4.646C5.26011 4.55211 5.13278 4.49937 5 4.49937C4.86722 4.49937 4.73989 4.55211 4.646 4.646C4.55211 4.73989 4.49937 4.86722 4.49937 5C4.49937 5.13278 4.55211 5.26011 4.646 5.354L7.293 8L4.646 10.646C4.59951 10.6925 4.56264 10.7477 4.53748 10.8084C4.51232 10.8692 4.49937 10.9343 4.49937 11C4.49937 11.0657 4.51232 11.1308 4.53748 11.1916C4.56264 11.2523 4.59951 11.3075 4.646 11.354C4.73989 11.4479 4.86722 11.5006 5 11.5006C5.06574 11.5006 5.13084 11.4877 5.19158 11.4625C5.25232 11.4374 5.30751 11.4005 5.354 11.354L8 8.707L10.646 11.354C10.6925 11.4005 10.7477 11.4374 10.8084 11.4625C10.8692 11.4877 10.9343 11.5006 11 11.5006C11.0657 11.5006 11.1308 11.4877 11.1916 11.4625C11.2523 11.4374 11.3075 11.4005 11.354 11.354C11.4005 11.3075 11.4374 11.2523 11.4625 11.1916C11.4877 11.1308 11.5006 11.0657 11.5006 11C11.5006 10.9343 11.4877 10.8692 11.4625 10.8084C11.4374 10.7477 11.4005 10.6925 11.354 10.646L8.707 8L11.354 5.354C11.4005 5.30751 11.4374 5.25232 11.4625 5.19158C11.4877 5.13084 11.5006 5.06574 11.5006 5C11.5006 4.93426 11.4877 4.86916 11.4625 4.80842C11.4374 4.74768 11.4005 4.69249 11.354 4.646C11.3075 4.59951 11.2523 4.56264 11.1916 4.53748C11.1308 4.51232 11.0657 4.49937 11 4.49937C10.9343 4.49937 10.8692 4.51232 10.8084 4.53748C10.7477 4.56264 10.6925 4.59951 10.646 4.646L8 7.293L5.354 4.646Z"
                  fill="#DC3545"
                />
              </svg>
              Not Trained
            </span>
          ));

        if (!shortDescriptionBlock && !trainingStatusBlock) {
          return null;
        }

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "12px",
              marginTop: "4px",
            }}
          >
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>{shortDescriptionBlock}</div>
            {trainingStatusBlock ? (
              <div style={{ flexShrink: 0, textAlign: "right" }}>{trainingStatusBlock}</div>
            ) : null}
          </div>
        );
      })()}

      {/* Step Details */}
      {stepDetailsData && (
        <p>
          {(() => {
            const stepDetails = stepDetailsData;
            // Always check if orders field exists (even if 0) to preserve sequence
            // This ensures sequence is preserved regardless of Qty being entered or not
            const hasOrdersField = stepDetails.some((step) => step.hasOwnProperty('orders'));

            if (hasOrdersField) {
              // Sort by orders field to preserve the saved sequence
              // This preserves the exact order as entered in Step Details Table, regardless of Qty
              // Fallback to created_at if orders are equal
              return [...stepDetails].sort((a, b) => {
                const orderA = parseInt(a.orders ?? 0, 10);
                const orderB = parseInt(b.orders ?? 0, 10);
                if (orderA !== orderB) return orderA - orderB;
                // Fallback to created_at in ascending order if orders are equal
                return new Date(a.created_at || 0) - new Date(b.created_at || 0);
              });
            } else {
              // If orders field is completely missing, sort by created_at in ascending order
              return [...stepDetails].sort((a, b) => {
                return new Date(a.created_at || 0) - new Date(b.created_at || 0);
              });
            }
          })().map((myStep, idx) => {
            const partNumber = myStep.part_info?.part_number || "";
            const description = myStep.part_info?.description || "";
            const qtyText =
              myStep.qty && myStep.qty !== 0 ? `Qty ${myStep.qty}` : "";
            const partText = partNumber ? ` (${partNumber})` : "";

            return (
              <span key={idx} style={{ whiteSpace: "nowrap" }}>
                {myStep.action}
                {partText} <RenderHtml content={description} inline /> {qtyText}
                &nbsp;
              </span>
            );
          })}
        </p>
      )}

      {/* BOM Table */}
      {bomTableTitle && (
        <h4>
          {bomTableTitle}
        </h4>
      )}

      {partsRequiredData && (
        <PartsTable
          data={partsRequiredData}
          itemCodeColumnTitle={itemCodeColumnTitle}
          dataFormat="step"
          autoSort={true}
          hideZeroQty={true}
          totalQtyToBuild={totalQtyToBuild}
        />
      )}

      {/* Step Children (Rich Text, Video, Form, Canvas, Attachment) */}
      <ChildrenRenderer
        children={step.children || []}
        parentContext={{
          sectionIndex,
          stepIndex,
          globalStepNumber,
          sectionId,
          stepId: step.tree_node_id,
          documentType,
          documentPartNumber,
          documentRevision,
        }}
        onNavigate={onNavigate}
        checkboxState={checkboxState}
        disabled={disabled}
        showCheckbox={
          showCheckbox && !showOnlySectionCheckboxes && !hideChildCheckboxes
        }
        showHeading={
          forceShowChildHeadings
            ? true
            : hideChildCheckboxes
              ? true
              : showOnlySectionCheckboxes
                ? true
                : null
        }
        allNodesData={allNodesData}
        checkboxLabelColor={checkboxLabelColor}
        isLock={isLock}
        isRevisionLocked={isRevisionLocked}
        hideChildHeadingsForTypes={hideChildHeadingsForTypes}
        enableCanvasStagePopup={enableCanvasStagePopup}
      />

      {/* Substeps */}
      {step.substeps?.map((substep, substepIndex) => (
        <SubstepNode
          key={substep.tree_node_id}
          substep={substep}
          substepIndex={substepIndex}
          stepIndex={stepIndex}
          sectionIndex={sectionIndex}
          globalStepNumber={globalStepNumber}
          sectionHeading={sectionHeading}
          stepHeading={stepHeading}
          sectionId={sectionId}
          stepId={step.tree_node_id}
          documentType={documentType}
          documentPartNumber={documentPartNumber}
          documentRevision={documentRevision}
          onNavigate={onNavigate}
          checkboxState={checkboxState}
          disabled={disabled}
          showCheckbox={
            showCheckbox && !showOnlySectionCheckboxes && !hideSubstepCheckboxes
          }
          showHeading={
            forceShowChildHeadings
              ? true
              : hideChildCheckboxes
                ? true
                : showOnlySectionCheckboxes
                  ? true
                  : null
          }
          allNodesData={allNodesData}
          checkboxLabelColor={checkboxLabelColor}
          hideChildCheckboxes={hideChildCheckboxes}
          isLock={isLock}
          isRevisionLocked={isRevisionLocked}
          hideChildHeadingsForTypes={hideChildHeadingsForTypes}
          enableCanvasStagePopup={enableCanvasStagePopup}
        />
      ))}
    </div>
  );
};

export default StepNode;
